import { logFrontendError } from "./log_error.js";
import { showToastMessage } from "./toast_message.js";

// The app answers its own requests with data or a JSON error. A redirect, or a failure without
// a JSON body, means something else replied (Cloud Armor, the load balancer, a login redirect,
// a crash), which the server logs don't show, so record what actually came back.
const BODY_PREVIEW_CHARS = 300;

function requestDetails(input, init){
    const method = (init?.method || input?.method || "GET").toUpperCase();
    const url = new URL(typeof input === "string" || input instanceof URL ? input : input.url, window.location.href);
    return { method, url };
}

export function isUnexpectedResponse(response){
    const contentType = response.headers.get("content-type") || "";
    return response.redirected || (!response.ok && !contentType.includes("application/json"));
}

function logUnexpectedResponse(response, method, url){
    const contentType = response.headers.get("content-type");
    response.clone().text()
        .catch(() => "")
        .then(body => logFrontendError(new Error(
            `${method} ${url.pathname} got an unexpected response: status ${response.status}, ` +
            `redirected ${response.redirected}${response.redirected ? ` to ${response.url}` : ""}, ` +
            `content-type ${contentType}, body starts: ${body.slice(0, BODY_PREVIEW_CHARS)}`)));
}

// login_required refuses a fetch with one of these instead of redirecting it to a page
const ACCESS_MESSAGES = {
    login_required: { message: "You're signed out, so that change wasn't saved.", label: "Sign in" },
    onboarding_required: { message: "Finish setting up your account to save changes.", label: "Finish setup" },
    terms_required: { message: "Accept the updated Terms to keep saving changes.", label: "Review Terms" }
};
let accessToastShown = false;

export function resetAccessToast(){
    accessToastShown = false;
}

function notifyAccessDenied(response){
    const contentType = response.headers.get("content-type") || "";
    if ((response.status !== 401 && response.status !== 403) || !contentType.includes("application/json")){
        return;
    }
    response.clone().json()
        .then(data => {
            const info = ACCESS_MESSAGES[data?.error];
            // A page with many pending saves would otherwise stack one toast per request
            if (!info || accessToastShown){
                return;
            }
            accessToastShown = true;
            const page = typeof data.redirect === "string" && data.redirect.startsWith("/") ? data.redirect : "/";
            showToastMessage(info.message, "warning", 10000, {
                label: info.label,
                onClick: () => { window.location.assign(page); }
            });
        })
        .catch(() => {});
}

export function monitorFetch(target = window){
    if (typeof target.fetch !== "function" || target.fetch.monitored){
        return;
    }
    const nativeFetch = target.fetch.bind(target);
    const monitored = async (input, init) => {
        const response = await nativeFetch(input, init);
        try {
            const { method, url } = requestDetails(input, init);
            if (url.origin === window.location.origin && url.pathname !== "/log_client_error"
                && isUnexpectedResponse(response)){
                logUnexpectedResponse(response, method, url);
            }
            if (url.origin === window.location.origin){
                notifyAccessDenied(response);
            }
        } catch (e) {
            console.error("Failed to check fetch response", e);
        }
        return response;
    };
    monitored.monitored = true;
    target.fetch = monitored;
}

monitorFetch();
